/**
 * quantity — quantidade de item, fracionária, num lugar só.
 *
 * POR QUE ISTO EXISTE: `quote_items.quantity` é `numeric` no Postgres e o PDV já
 * gravava 3 casas (1,5 kg de fiação; 2,5 h de mão de obra), mas o card de item
 * do ORÇAMENTO forçava inteiro ≥ 1 (`Math.floor`/`parseInt`). O mesmo produto
 * podia ser vendido fracionado no balcão e não podia ser orçado — e as duas
 * telas gravam na MESMA coluna, então cada uma arredondando à sua maneira daria
 * divergência entre o orçamento e o pedido gerado dele.
 *
 * `roundQuantity` mora aqui e é reexportado por `quickSale` (que era o dono
 * anterior), para que exista uma implementação só.
 */

/** Casas decimais aceitas — o que `numeric` guarda e o que a tela exibe. */
export const QUANTITY_DECIMALS = 3;

/**
 * Menor quantidade válida. Zero e negativo continuam inválidos (um item de
 * quantidade 0 não é um item; negativo não é uma venda).
 */
export const MIN_QUANTITY = 0.001;

/** Arredonda para {@link QUANTITY_DECIMALS} casas, sem o erro de ponto flutuante. */
export function roundQuantity(value: number): number {
  const factor = 10 ** QUANTITY_DECIMALS;
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
}

export interface ParsedQuantity {
  /** `false` quando o texto não é um número utilizável ou é ≤ 0. */
  ok: boolean;
  /** `null` quando o campo está VAZIO (o usuário apagou para digitar de novo). */
  value: number | null;
}

/**
 * Lê uma quantidade digitada em pt-BR ("1,5"). Campo vazio devolve
 * `{ ok: true, value: null }` — é estado de digitação, não erro; quem chama
 * decide se deixa passar (enquanto digita) ou repõe o padrão (no blur).
 */
export function parseQuantityInput(raw: string | number | null | undefined): ParsedQuantity {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw < MIN_QUANTITY) return { ok: false, value: null };
    return { ok: true, value: roundQuantity(raw) };
  }

  const text = String(raw ?? '').trim();
  if (!text) return { ok: true, value: null };

  // Validação ESTRITA antes de converter. O `parseFloat` anterior parava no
  // primeiro caractere inválido em vez de recusar: `'5abc'` virava 5 e
  // `'1,5,5'` virava 1,5 — o usuário digitava uma coisa, o app gravava outra,
  // sem nenhum aviso. Recusar deixa o erro visível para quem digitou.
  //
  // Um separador só, vírgula OU ponto: o teclado numérico do Android manda
  // ponto e o teclado pt-BR manda vírgula (armadilha #71).
  if (!/^\d+([.,]\d+)?$/.test(text)) return { ok: false, value: null };

  const n = Number(text.replace(',', '.'));
  if (!Number.isFinite(n) || n < MIN_QUANTITY) return { ok: false, value: null };
  return { ok: true, value: roundQuantity(n) };
}

/**
 * O texto digitado tem MAIS casas decimais do que o app guarda?
 *
 * ─── O DEFEITO QUE ISTO EXISTE PARA MATAR ───────────────────────────────────
 *
 * O campo de quantidade do card de item guarda o texto num estado LOCAL (para o
 * usuário poder apagar o "1" enquanto digita) e só recoloca o texto formatado
 * quando `item.quantity` MUDA. Digitar "1,0001" numa linha que já está em 1
 * fecha o ciclo pelo lado errado: `parseQuantityInput` arredonda para 1, o
 * modelo já era 1, **nada muda**, o efeito de sincronia não dispara — e a tela
 * segue mostrando "1,0001" enquanto o documento guarda 1. Sem erro, sem toast:
 * o número na tela simplesmente não é o número que vai ao PDF e ao banco.
 *
 * É fácil de produzir sem querer num campo de 32px com teclado numérico (tecla
 * repetida, autocorreção), e some sozinho ao reabrir a tela — porque a
 * remontagem reinicializa o texto a partir do modelo. Foi exatamente o relato:
 * "aconteceu uma vez, fechei o app e não aconteceu mais".
 */
export function quantityInputExceedsPrecision(raw: string | null | undefined): boolean {
  const text = String(raw ?? '');
  const separator = text.search(/[.,]/);
  if (separator < 0) return false;
  return text.length - separator - 1 > QUANTITY_DECIMALS;
}

/**
 * Quantidade → texto do campo, em pt-BR. Inteiro sai sem casas ("3", não
 * "3,000"): o caso comum não pode ficar mais feio por causa do fracionário.
 */
export function formatQuantityInput(value: number | string | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const rounded = roundQuantity(n);
  return String(rounded).replace('.', ',');
}

/**
 * Quantidade para EXIBIÇÃO (PDF, planilha, resumo). Mesma regra do campo — o
 * `String(quantity)` que o PDF usava imprimia "1.5" com PONTO, que em pt-BR se
 * lê como mil e quinhentos.
 */
export function formatQuantityDisplay(value: number | string | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return roundQuantity(n).toLocaleString('pt-BR', { maximumFractionDigits: QUANTITY_DECIMALS });
}
